/* eslint-disable react-refresh/only-export-components -- provider + hooks co-locados de propósito, como pageChrome.tsx */
/** Estado do assistente: aberto ou fechado, largura, contexto da tela e a
 *  conversa atual, com o motor que envia a pergunta e acompanha o fluxo.
 *
 *  Três contextos de propósito. O da casca muda pouco e alimenta o botão e a
 *  coluna. O do contexto da tela tem leitura e escrita separadas, no molde de
 *  `pageChrome.tsx`, para a tela que publica não re-renderizar o painel a cada
 *  tecla. O da conversa muda a cada trecho de texto que chega e só o painel o lê.
 *
 *  O motor vive aqui, e não no painel, para fechar a coluna no meio de uma
 *  resposta não abortar o fluxo: a resposta continua chegando e está inteira na
 *  próxima abertura. `aberto`, `largura` e a conversa atual ficam em localStorage
 *  com a mesma proteção da barra lateral.
 *
 *  Sem a permissão ou com o assistente desligado no runtime, o botão e a coluna
 *  devolvem null e nada do assistente existe no DOM; os contextos ficam de pé só
 *  para as telas publicarem sem se perguntar se o assistente existe. */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode, type RefObject,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCan } from '@/core/auth/useCan'
import { useI18n } from '@/core/i18n/useI18n'
import { ApiError } from '@/core/api/types'
import {
  CODIGO_LIMITE_DIARIO, assistenteApi, chaveDaConversa, chaveDeTodasAsListas, useConversa,
  type ContextoDaTela, type ConversaDetalhe, type ConversaResumo, type MensagemDaConversa,
} from './api'
import { fluxoEmAndamento, fluxoTerminou, type EstadoDoFluxo } from './fluxoReducer'
import { depuracaoDeSqlLigada } from './depuracao'
import { semTurno } from './turnos'
import { useFluxo } from './useFluxo'

export const LARGURA_PADRAO = 400
export const LARGURA_MIN = 360
export const LARGURA_MAX = 560
export const PERMISSAO_DO_ASSISTENTE = 'assistente.usar'

const CHAVE_ABERTO = 'assistente-open'
const CHAVE_LARGURA = 'assistente-width'
const CHAVE_CONVERSA = 'assistente-conversa'
// Preferências de leitura, desde 07/09/2026: os cartões de etapa e a linha Ver
// consulta podem ser escondidos por quem só quer a resposta.
const CHAVE_ETAPAS = 'assistente-mostrar-etapas'
const CHAVE_CONSULTAS = 'assistente-mostrar-consultas'

export type VistaDoPainel = 'conversa' | 'historico'
export type AbaDoHistorico = 'ativas' | 'arquivadas'

export interface EstadoDoAssistente {
  /** Permissão e configuração de runtime: sem os dois, nada do assistente existe. */
  ativo: boolean
  aberto: boolean
  largura: number
  vista: VistaDoPainel
  abrir: () => void
  fechar: () => void
  alternar: () => void
  setLargura: (largura: number) => void
  setVista: (vista: VistaDoPainel) => void
  /** Ativas ou arquivadas, dentro do histórico. */
  abaDoHistorico: AbaDoHistorico
  setAbaDoHistorico: (aba: AbaDoHistorico) => void
  /** Mostrar os cartões de etapa nas respostas terminadas. Persistido. */
  mostrarEtapas: boolean
  setMostrarEtapas: (ligado: boolean) => void
  /** Quem testa vê o SQL: pelo runtime-config ou pela marca no navegador. Sem
   *  isto, a linha Ver consulta e o interruptor dela não existem na tela. */
  podeVerConsultas: boolean
  /** Mostrar a linha Ver consulta abaixo das respostas. Persistido; só vale
   *  com `podeVerConsultas`. */
  mostrarConsultas: boolean
  setMostrarConsultas: (ligado: boolean) => void
  /** O botão da barra superior, para o Esc devolver o foco. */
  botaoRef: RefObject<HTMLButtonElement>
  /** Sobe a cada abertura pedida pelo usuário, botão ou atalho. O campo de texto
   *  foca quando este número muda, e não ao montar: recarregar a página com o
   *  painel aberto não pode roubar o foco do documento. */
  focoPedido: number
}

export interface MensagemVista extends MensagemDaConversa {
  /** Presente só na resposta que ainda está chegando. */
  fluxo?: EstadoDoFluxo
}

export interface ConversaDoAssistente {
  conversaAtual: string | null
  titulo: string | null
  carregando: boolean
  erroAoCarregar: boolean
  mensagens: MensagemVista[]
  fluxo: EstadoDoFluxo
  emAndamento: boolean
  limite: { voltaEm: string } | null
  contexto: ContextoDaTela | null
  contextoLigado: boolean
  setContextoLigado: (ligado: boolean) => void
  enviar: (texto: string) => void
  parar: () => void
  /** Reenvia a última pergunta no lugar do turno que falhou ou foi parado. */
  tentarDeNovo: () => void
  abrirConversa: (uuid: string) => void
  novaConversa: () => void
  /** Editar e reenviar: o texto de uma pergunta antiga vai para o campo. */
  rascunho: string | null
  editarPergunta: (texto: string) => void
  consumirRascunho: () => void
  /** Tira da conversa o turno da resposta: a pergunta e a resposta. */
  apagarTurno: (mensagemUuid: string) => Promise<void>
}

const AssistenteCtx = createContext<EstadoDoAssistente | null>(null)
const ConversaCtx = createContext<ConversaDoAssistente | null>(null)
const ContextoCtx = createContext<ContextoDaTela | null>(null)
export const SetContextoCtx = createContext<(contexto: ContextoDaTela | null) => void>(() => {})

function ler(chave: string): string | null {
  try {
    return localStorage.getItem(chave)
  } catch {
    return null
  }
}

function gravar(chave: string, valor: string | null) {
  try {
    if (valor === null) localStorage.removeItem(chave)
    else localStorage.setItem(chave, valor)
  } catch {
    /* ignore */
  }
}

export const limitarLargura = (n: number) => Math.min(LARGURA_MAX, Math.max(LARGURA_MIN, Math.round(n)))

function lerLargura(): number {
  const n = Number(ler(CHAVE_LARGURA))
  return Number.isFinite(n) && n > 0 ? limitarLargura(n) : LARGURA_PADRAO
}

export function assistenteLigadoNoRuntime(): boolean {
  return Boolean(window.__TRANSGRAOS_CONFIG__?.assistenteEnabled)
}

/** Campo de texto que não é o do assistente: o atalho não pode roubar o Ctrl+J
 *  de quem está digitando num filtro. */
function ehCampoDeTextoAlheio(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false
  if (alvo.hasAttribute('data-assistente-composer')) return false
  if (alvo.isContentEditable) return true
  const tag = alvo.tagName
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (tag === 'INPUT') {
    const tipo = (alvo as HTMLInputElement).type
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'range', 'file', 'color'].includes(tipo)
  }
  return false
}

export function AssistenteProvider({ children }: { children: ReactNode }) {
  const can = useCan()
  const ativo = can(PERMISSAO_DO_ASSISTENTE) && assistenteLigadoNoRuntime()

  const [aberto, setAberto] = useState<boolean>(() => ler(CHAVE_ABERTO) === '1')
  const [largura, setLarguraEstado] = useState<number>(lerLargura)
  const [vista, setVista] = useState<VistaDoPainel>('conversa')
  const [abaDoHistorico, setAbaDoHistorico] = useState<AbaDoHistorico>('ativas')
  // Ligados por padrão: só quem desliga grava '0'.
  const [mostrarEtapas, setMostrarEtapas] = useState<boolean>(() => ler(CHAVE_ETAPAS) !== '0')
  const [mostrarConsultasPreferido, setMostrarConsultas] = useState<boolean>(() => ler(CHAVE_CONSULTAS) !== '0')
  const podeVerConsultas = depuracaoDeSqlLigada()
  const mostrarConsultas = podeVerConsultas && mostrarConsultasPreferido
  const [contexto, setContexto] = useState<ContextoDaTela | null>(null)
  const [focoPedido, setFocoPedido] = useState(0)
  const botaoRef = useRef<HTMLButtonElement>(null)
  const abertoRef = useRef(aberto)
  abertoRef.current = aberto

  // A preferência vai para o localStorage depois do estado mudar, nunca de dentro
  // do updater: o StrictMode chama o updater duas vezes em desenvolvimento.
  useEffect(() => {
    gravar(CHAVE_ABERTO, aberto ? '1' : '0')
  }, [aberto])
  useEffect(() => {
    gravar(CHAVE_LARGURA, String(largura))
  }, [largura])
  useEffect(() => {
    gravar(CHAVE_ETAPAS, mostrarEtapas ? '1' : '0')
  }, [mostrarEtapas])
  useEffect(() => {
    gravar(CHAVE_CONSULTAS, mostrarConsultasPreferido ? '1' : '0')
  }, [mostrarConsultasPreferido])

  const abrir = useCallback(() => {
    setAberto(true)
    setFocoPedido((n) => n + 1)
  }, [])
  const fechar = useCallback(() => setAberto(false), [])
  const alternar = useCallback(() => {
    const proximo = !abertoRef.current
    setAberto(proximo)
    if (proximo) setFocoPedido((n) => n + 1)
  }, [])
  const setLargura = useCallback((n: number) => setLarguraEstado(limitarLargura(n)), [])

  // Ctrl+J, ou Cmd+J no Mac, abre e fecha de qualquer tela. Ignora quando o foco
  // está num campo de texto que não é o do assistente.
  useEffect(() => {
    if (!ativo) return
    function aoTeclar(e: KeyboardEvent) {
      if (e.key.toLowerCase() !== 'j' || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return
      if (ehCampoDeTextoAlheio(e.target)) return
      e.preventDefault()
      alternar()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [ativo, alternar])

  const estado = useMemo<EstadoDoAssistente>(() => ({
    ativo, aberto, largura, vista, abrir, fechar, alternar, setLargura, setVista,
    abaDoHistorico, setAbaDoHistorico, mostrarEtapas, setMostrarEtapas,
    podeVerConsultas, mostrarConsultas, setMostrarConsultas,
    botaoRef, focoPedido,
  }), [
    ativo, aberto, largura, vista, abrir, fechar, alternar, setLargura, focoPedido,
    abaDoHistorico, mostrarEtapas, podeVerConsultas, mostrarConsultas,
  ])

  return (
    <AssistenteCtx.Provider value={estado}>
      <SetContextoCtx.Provider value={setContexto}>
        <ContextoCtx.Provider value={contexto}>
          <MotorDaConversa ativo={ativo} contexto={contexto} setVista={setVista}>
            {children}
          </MotorDaConversa>
        </ContextoCtx.Provider>
      </SetContextoCtx.Provider>
    </AssistenteCtx.Provider>
  )
}

/** Envia a pergunta, acompanha o fluxo e consolida a resposta na cache da
 *  conversa. Nova conversa só nasce no POST da primeira mensagem, para não
 *  acumular conversas vazias. Inerte enquanto `ativo` é falso: a consulta da
 *  conversa fica desligada e nada é enviado. */
function MotorDaConversa({ ativo, contexto, setVista, children }: {
  ativo: boolean
  contexto: ContextoDaTela | null
  setVista: (vista: VistaDoPainel) => void
  children: ReactNode
}) {
  const { lang } = useI18n()
  const qc = useQueryClient()
  const { estado: fluxo, iniciar, preparar, parar: pararFluxo, limpar, falhar } = useFluxo()
  const [conversaAtual, setConversaAtual] = useState<string | null>(() => ler(CHAVE_CONVERSA))
  const [pendentes, setPendentes] = useState<MensagemDaConversa[]>([])
  const [limite, setLimite] = useState<{ voltaEm: string } | null>(null)
  const [contextoLigado, setContextoLigado] = useState(true)
  const [rascunho, setRascunho] = useState<string | null>(null)
  const ultimoTextoRef = useRef<string | null>(null)
  const conversaDoFluxoRef = useRef<string | null>(null)

  // Espelhos síncronos para as callbacks estáveis lerem o valor do momento.
  const fluxoRef = useRef(fluxo)
  fluxoRef.current = fluxo
  const pendentesRef = useRef(pendentes)
  pendentesRef.current = pendentes
  const contextoRef = useRef(contexto)
  contextoRef.current = contexto

  const consulta = useConversa(conversaAtual, ativo)

  const definirConversa = useCallback((uuid: string | null) => {
    setConversaAtual(uuid)
    gravar(CHAVE_CONVERSA, uuid)
  }, [])

  // A conversa gravada não é mais desta sessão, ou foi apagada: começa vazio em
  // vez de mostrar um erro para sempre.
  useEffect(() => {
    if (!consulta.isError || !conversaAtual) return
    const erro = consulta.error
    if (erro instanceof ApiError && (erro.status === 404 || erro.status === 403)) definirConversa(null)
  }, [consulta.isError, consulta.error, conversaAtual, definirConversa])

  /** Leva o par pergunta e resposta para a cache da conversa e zera o fluxo. A
   *  resposta só entra se trouxe alguma coisa; erro seco fica só na pergunta. */
  const consolidar = useCallback(() => {
    const uuid = conversaDoFluxoRef.current
    const estado = fluxoRef.current
    const pendentesAgora = pendentesRef.current
    if (uuid) {
      const trouxeAlgo = estado.texto || estado.etapas.length || estado.consultas.length
      const resposta: MensagemDaConversa | null = estado.status !== 'ocioso' && trouxeAlgo
        ? {
          uuid: estado.mensagemUuid ?? `local-${Date.now()}`,
          papel: 'assistente',
          texto: estado.texto,
          etapas: estado.etapas,
          consultas: estado.consultas,
          created_at: new Date().toISOString(),
        }
        : null
      qc.setQueryData<ConversaDetalhe>(chaveDaConversa(uuid), (atual) => {
        const base = atual ?? { uuid, titulo: null, mensagens: [] }
        return {
          ...base,
          titulo: estado.titulo ?? base.titulo,
          mensagens: [...base.mensagens, ...pendentesAgora, ...(resposta ? [resposta] : [])],
        }
      })
      void qc.invalidateQueries({ queryKey: chaveDeTodasAsListas() })
    }
    conversaDoFluxoRef.current = null
    setPendentes([])
    limpar()
  }, [qc, limpar])

  /** Descarta o turno em tela sem gravar nada: é o Tentar de novo, que refaz a
   *  pergunta no lugar da resposta que falhou em vez de empilhar as duas. */
  const descartarTurno = useCallback(() => {
    conversaDoFluxoRef.current = null
    setPendentes([])
    limpar()
  }, [limpar])

  // Ao chegar `fim`, a resposta vira mensagem gravada.
  useEffect(() => {
    if (fluxo.status === 'concluido') consolidar()
  }, [fluxo.status, consolidar])

  // O título chega pelo evento e atualiza a lista na hora.
  useEffect(() => {
    const titulo = fluxo.titulo
    const uuid = conversaDoFluxoRef.current
    if (!titulo || !uuid) return
    qc.setQueryData<ConversaDetalhe>(chaveDaConversa(uuid), (atual) => (atual ? { ...atual, titulo } : atual))
    qc.setQueriesData<ConversaResumo[]>({ queryKey: chaveDeTodasAsListas() }, (lista) =>
      lista?.map((c) => (c.uuid === uuid ? { ...c, titulo } : c)))
  }, [fluxo.titulo, qc])

  // Teto diário: a pergunta não foi aceita, o campo desabilita e diz quando volta.
  useEffect(() => {
    const erro = fluxo.erro
    if (fluxo.status !== 'erro' || erro?.code !== CODIGO_LIMITE_DIARIO) return
    setLimite({ voltaEm: erro.voltaEm ?? new Date(Date.now() + 60 * 60_000).toISOString() })
    descartarTurno()
  }, [fluxo.status, fluxo.erro, descartarTurno])

  useEffect(() => {
    if (!limite) return
    const ms = new Date(limite.voltaEm).getTime() - Date.now()
    if (!Number.isFinite(ms)) return
    const id = setTimeout(() => setLimite(null), Math.max(1000, ms))
    return () => clearTimeout(id)
  }, [limite])

  const enviar = useCallback(async (textoCru: string, opcoes: { substituirTurno?: boolean } = {}) => {
    const texto = textoCru.trim()
    if (!ativo || !texto || fluxoEmAndamento(fluxoRef.current.status) || limite) return
    if (fluxoTerminou(fluxoRef.current.status)) {
      if (opcoes.substituirTurno) descartarTurno()
      else consolidar()
    }
    ultimoTextoRef.current = texto

    const contextoEnviado = contextoLigado ? contextoRef.current : null
    // O interruptor desligado vale para uma pergunta só; volta na próxima.
    if (!contextoLigado) setContextoLigado(true)

    setPendentes([{
      uuid: `local-${Date.now()}`, papel: 'usuario', texto, etapas: [], consultas: [],
      created_at: new Date().toISOString(),
    }])
    // O sinal de trabalho aparece agora, e não depois de criar a conversa.
    preparar()

    let uuid = conversaAtual
    if (!uuid) {
      try {
        const criada = await assistenteApi.criarConversa()
        uuid = criada.uuid
        qc.setQueryData<ConversaDetalhe>(chaveDaConversa(uuid), { uuid, titulo: null, mensagens: [] })
        definirConversa(uuid)
      } catch {
        falhar({ code: 'CRIAR_CONVERSA', mensagem: '' })
        return
      }
    }
    conversaDoFluxoRef.current = uuid
    void iniciar(uuid, { texto, contexto: contextoEnviado, idioma: lang })
  }, [ativo, conversaAtual, contextoLigado, limite, lang, qc, iniciar, preparar, falhar, consolidar, descartarTurno, definirConversa])

  const parar = useCallback(() => {
    if (!fluxoEmAndamento(fluxoRef.current.status)) return
    pararFluxo()
    const uuid = conversaDoFluxoRef.current
    if (uuid) void assistenteApi.pararTurno(uuid).catch(() => undefined)
  }, [pararFluxo])

  const tentarDeNovo = useCallback(() => {
    if (ultimoTextoRef.current) void enviar(ultimoTextoRef.current, { substituirTurno: true })
  }, [enviar])

  const abrirConversa = useCallback((uuid: string) => {
    if (fluxoEmAndamento(fluxoRef.current.status)) parar()
    consolidar()
    definirConversa(uuid)
    setVista('conversa')
  }, [parar, consolidar, definirConversa, setVista])

  const novaConversa = useCallback(() => {
    if (fluxoEmAndamento(fluxoRef.current.status)) parar()
    consolidar()
    definirConversa(null)
    setVista('conversa')
  }, [parar, consolidar, definirConversa, setVista])

  const editarPergunta = useCallback((texto: string) => setRascunho(texto), [])
  const consumirRascunho = useCallback(() => setRascunho(null), [])

  /** Apaga no servidor e recorta a cache do mesmo jeito que ele: a resposta e
   *  a pergunta que a antecede. Resposta ainda sem uuid do servidor não tem o
   *  que apagar lá, e o botão nem aparece para ela. */
  const apagarTurno = useCallback(async (mensagemUuid: string) => {
    const uuid = conversaAtual
    if (!uuid || mensagemUuid.startsWith('local-') || mensagemUuid === 'fluxo') return
    await assistenteApi.apagarTurno(mensagemUuid)
    qc.setQueryData<ConversaDetalhe>(chaveDaConversa(uuid), (atual) =>
      (atual ? { ...atual, mensagens: semTurno(atual.mensagens, mensagemUuid) } : atual))
    void qc.invalidateQueries({ queryKey: chaveDeTodasAsListas() })
  }, [conversaAtual, qc])

  const mensagens = useMemo<MensagemVista[]>(() => {
    const lista: MensagemVista[] = [...(consulta.data?.mensagens ?? []), ...pendentes]
    if (fluxo.status !== 'ocioso') {
      lista.push({
        uuid: fluxo.mensagemUuid ?? 'fluxo',
        papel: 'assistente',
        texto: fluxo.texto,
        etapas: fluxo.etapas,
        consultas: fluxo.consultas,
        created_at: '',
        fluxo,
      })
    }
    return lista
  }, [consulta.data, pendentes, fluxo])

  const valor = useMemo<ConversaDoAssistente>(() => ({
    conversaAtual,
    titulo: fluxo.titulo ?? consulta.data?.titulo ?? null,
    carregando: Boolean(conversaAtual) && consulta.isLoading,
    erroAoCarregar: consulta.isError,
    mensagens,
    fluxo,
    emAndamento: fluxoEmAndamento(fluxo.status),
    limite,
    contexto,
    contextoLigado,
    setContextoLigado,
    enviar: (texto) => void enviar(texto),
    parar,
    tentarDeNovo,
    abrirConversa,
    novaConversa,
    rascunho,
    editarPergunta,
    consumirRascunho,
    apagarTurno,
  }), [
    conversaAtual, consulta.data?.titulo, consulta.isLoading, consulta.isError, mensagens, fluxo,
    limite, contexto, contextoLigado, enviar, parar, tentarDeNovo, abrirConversa, novaConversa,
    rascunho, editarPergunta, consumirRascunho, apagarTurno,
  ])

  return <ConversaCtx.Provider value={valor}>{children}</ConversaCtx.Provider>
}

export function useAssistente(): EstadoDoAssistente {
  const ctx = useContext(AssistenteCtx)
  if (!ctx) throw new Error('useAssistente deve ser usado dentro de <AssistenteProvider>')
  return ctx
}

export function useConversaDoAssistente(): ConversaDoAssistente {
  const ctx = useContext(ConversaCtx)
  if (!ctx) throw new Error('useConversaDoAssistente deve ser usado dentro de <AssistenteProvider>')
  return ctx
}

/** Lido pelo painel: o último contexto publicado pela tela atual. */
export const useContextoDaTela = () => useContext(ContextoCtx)
